import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useSpring, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  TrendingDown, Upload, Zap, MapPin, Users, Home, Sun, ChevronRight, ChevronLeft,
  MessageCircle, Mail, Loader2, X, AlertTriangle, Check, Sparkles,
  Car, Flame, Waves, Wind, Compass, FileText, Award, ArrowUp, TrendingUp,
} from "lucide-react";
import { apiClient } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import RoofMap from "../components/RoofMap";
import { trackLead as fbTrackLead, trackInitiateCheckout, trackCustom } from "../lib/fbpixel";
import { trackEvent as gaTrackEvent, trackLead as gaTrackLead } from "../lib/gtag";
import { setTag as clarityTag, trackEvent as clarityEvent, upgrade as clarityUpgrade } from "../lib/clarity";

/* MockupHomeV4 — Embedded loss-aversion intake with 3 progressive layers.
   Layer 1: 4 quick questions  → first savings reveal  (accuracy 60%)
   Layer 2: 3 refinement Qs    → refined reveal w/ Δ  (accuracy 85%)
   Layer 3: Mapbox + fiscal     → premium reveal       (accuracy 95%)
   Each layer ends in an exit ramp (WhatsApp/email lead) so we capture leads
   at every commitment level. Mounted at `/v4`. */

const STR = {
  nl: {
    nav_login: "Inloggen",
    hero_pre: "DoctorSol Calculator",
    hero_h1_1: "Stop met geld verliezen,", hero_h1_2: "iedere dag.",
    hero_p: "De zon schijnt boven Spanje — maar jij betaalt nog steeds vol tarief aan het net. Bereken in 60 seconden hoeveel je écht verliest.",
    hero_stat_label: "huiseigenaren weten nu hoeveel geld ze verliezen", counter_live: "live",
    path_q: "Snelle vragen (60 sec)", path_bill: "Factuur uploaden",
    upload_drop: "Sleep je elektriciteitsfactuur hierheen",
    upload_or: "of klik om te kiezen · PDF · JPG · PNG",
    // Layer 1
    q1_label: "Wat betaal je gemiddeld per maand?", q1_suffix: "€/maand",
    q1_hint: "Bijna alles gaat naar het net. Per jaar verlies je",
    q2_label: "Waar woon je?", q2_placeholder: "Bijv. Madrid, Valencia, Marbella…",
    q3_label: "Hoeveel bewoners?",
    q4_label: "Hoe groot is je woning?",
    q4_o1: "< 80 m²", q4_o2: "80-150 m²", q4_o3: "150-250 m²", q4_o4: "> 250 m²",
    // Layer 2
    q5_label: "Type aansluiting (op je meterkast)",
    q5_o1_l: "1-fase (mono)", q5_o1_s: "Meeste woningen — tot ±9 kW",
    q5_o2_l: "3-fase (trifásico)", q5_o2_s: "Vaak villa's, ≥ 9,2 kW",
    q5_o3_l: "Weet ik niet", q5_o3_s: "Wij maken een conservatieve schatting",
    q6_label: "Wat voor dak heb je?",
    q6_o1_l: "Plat dak", q6_o1_s: "Panelen op frame — ~92% rendement",
    q6_o2_l: "Hellend dak", q6_o2_s: "Standaard — 100% rendement",
    q6_o3_l: "Weet ik niet", q6_o3_s: "Conservatieve schatting",
    q7_label: "Wat ga je nog aanschaffen?",
    q7_hint: "Apparaten die je nu al hebt zitten al in je rekening verwerkt.",
    q7_ev: "Elektrische auto", q7_hp: "Warmtepomp", q7_pool: "Zwembad", q7_ac: "Airco",
    // Layer 3
    q8_label: "Teken je dakvlak op de kaart",
    q8_hint: "Zoek je adres, zoom in tot je je dak ziet, en klik de hoekpunten van het bruikbare dakoppervlak.",
    q9_label: "Ben je fiscaal resident in Spanje?",
    q9_yes: "Ja, ik woon in Spanje", q9_yes_s: "Recht op IRPF-aftrek tot 60%",
    q9_no: "Nee, niet-resident",     q9_no_s: "Wel IBI-korting + EU-fondsen",
    // Common
    next: "Volgende", back: "Terug",
    calc: "Bereken mijn besparing", calc_busy: "Berekenen…",
    refine: "Verfijnen", refine_busy: "Verfijnen…",
    premium: "Genereer mijn PDF-dossier", premium_busy: "Genereren…",
    // Reveals
    r1_kicker: "Je verlies tot nu toe", r1_h1: "Je laat dit geld liggen.",
    r1_p: "Geschat op basis van je rekening. Zonnepanelen dekken ~70% van je verbruik.",
    r2_kicker: "Verfijnde berekening", r2_h1: "Je besparing is groter dan we dachten.",
    r2_p: "Met je extra details kunnen we nauwkeuriger rekenen — inclusief regio, type aansluiting en toekomstig verbruik.",
    r3_kicker: "Premium dossier · 95% accuraat", r3_h1: "Je complete besparingsplan.",
    r3_p: "Inclusief subsidies (IRPF, IBI, Next Generation) en je werkelijke dakoppervlak.",
    rev_month: "Per maand", rev_year: "Per jaar", rev_10y: "Over 10 jaar",
    rev_subsidies: "Subsidies",
    rev_lost: "Geld verloren in laatste 12 maanden door geen panelen:",
    delta_label: "Update",
    // Accuracy meter
    acc_label: "Accuratesse",
    acc_60: "Ruwe schatting", acc_85: "Verfijnd", acc_95: "Premium",
    upgrade_to_85_btn: "Verfijn naar 85% — 3 vragen extra",
    upgrade_to_95_btn: "Complete PDF — 2 vragen extra",
    skip_to_lead: "Stuur me wat ik nu heb →",
    // CTA
    cta_h2: "Stuur me het volledige rapport — gratis",
    cta_p: "Inclusief alle subsidies in jouw regio. Geen creditcard. Geen verkooppraatjes.",
    cta_wa_label: "WhatsApp", cta_wa_ph: "+34 ...",
    cta_em_label: "E-mail",   cta_em_ph: "jij@email.es",
    cta_send_wa: "Stuur naar WhatsApp", cta_send_em: "Stuur naar e-mail", cta_or: "of",
    sent_ok: "Verzonden — check je inbox/WhatsApp binnen 24u.",
    again: "Opnieuw berekenen",
    upsell_h3: "Wat kost een zonnepaneleninstallatie voor jou?",
    upsell_sub: "Ontvang een vrijblijvende Dr. Sol offerte.",
    upsell_btn: "Klik hier →",
    err_q1: "Schuif het bedrag naar je gemiddelde rekening",
    err_q2: "Vul een stad of postcode in",
    err_pick: "Maak een keuze",
    err_polygon: "Klik minstens 3 punten op je dak",
    err_contact: "Vul een e-mail of WhatsApp-nummer in",
  },
  es: {
    nav_login: "Acceder",
    hero_pre: "Calculadora DoctorSol",
    hero_h1_1: "Deja de perder dinero,", hero_h1_2: "cada día.",
    hero_p: "El sol brilla sobre España — pero tú sigues pagando tarifa plena a la red. Calcula en 60 segundos cuánto pierdes realmente.",
    hero_stat_label: "propietarios saben ahora cuánto dinero pierden", counter_live: "en vivo",
    path_q: "Preguntas rápidas (60 seg)", path_bill: "Subir factura",
    upload_drop: "Suelta tu factura eléctrica aquí",
    upload_or: "o haz clic para elegir · PDF · JPG · PNG",
    q1_label: "¿Cuánto pagas de media al mes?", q1_suffix: "€/mes",
    q1_hint: "Casi todo va a la red. Al año pierdes",
    q2_label: "¿Dónde vives?", q2_placeholder: "Ej. Madrid, Valencia, Marbella…",
    q3_label: "¿Cuántas personas?",
    q4_label: "¿Tamaño de la vivienda?",
    q4_o1: "< 80 m²", q4_o2: "80-150 m²", q4_o3: "150-250 m²", q4_o4: "> 250 m²",
    q5_label: "Tipo de conexión (en tu cuadro eléctrico)",
    q5_o1_l: "Monofásica", q5_o1_s: "La mayoría — hasta ±9 kW",
    q5_o2_l: "Trifásica",  q5_o2_s: "Villas a menudo, ≥ 9,2 kW",
    q5_o3_l: "No lo sé",   q5_o3_s: "Hacemos una estimación conservadora",
    q6_label: "¿Qué tipo de tejado tienes?",
    q6_o1_l: "Tejado plano",   q6_o1_s: "Paneles en estructura — ~92% rendimiento",
    q6_o2_l: "Tejado inclinado", q6_o2_s: "Estándar — 100% rendimiento",
    q6_o3_l: "No lo sé",       q6_o3_s: "Estimación conservadora",
    q7_label: "¿Qué planeas comprar?",
    q7_hint: "Lo que ya tienes está en tu factura — no marques lo actual.",
    q7_ev: "Coche eléctrico", q7_hp: "Bomba de calor", q7_pool: "Piscina", q7_ac: "Aire acond.",
    q8_label: "Dibuja tu tejado en el mapa",
    q8_hint: "Busca tu dirección, haz zoom hasta ver tu tejado y marca las esquinas del área útil.",
    q9_label: "¿Eres residente fiscal en España?",
    q9_yes: "Sí, vivo en España", q9_yes_s: "Derecho a deducción IRPF hasta 60%",
    q9_no: "No, no residente",    q9_no_s: "Sí bonificación IBI + fondos UE",
    next: "Siguiente", back: "Atrás",
    calc: "Calcular mi ahorro", calc_busy: "Calculando…",
    refine: "Afinar", refine_busy: "Afinando…",
    premium: "Generar mi PDF", premium_busy: "Generando…",
    r1_kicker: "Tu pérdida hasta ahora", r1_h1: "Estás dejando este dinero sobre la mesa.",
    r1_p: "Estimado según tu factura. Los paneles cubren ~70% del consumo.",
    r2_kicker: "Cálculo afinado", r2_h1: "Tu ahorro es mayor de lo previsto.",
    r2_p: "Con tus detalles afinamos el cálculo — región, conexión y consumo futuro.",
    r3_kicker: "Dossier premium · 95% preciso", r3_h1: "Tu plan completo de ahorro.",
    r3_p: "Incluye subvenciones (IRPF, IBI, Next Generation) y tu superficie real.",
    rev_month: "Al mes", rev_year: "Al año", rev_10y: "En 10 años",
    rev_subsidies: "Subvenciones",
    rev_lost: "Dinero perdido en 12 meses por no tener paneles:",
    delta_label: "Actualización",
    acc_label: "Precisión",
    acc_60: "Estimación rápida", acc_85: "Afinada", acc_95: "Premium",
    upgrade_to_85_btn: "Afinar al 85% — 3 preguntas más",
    upgrade_to_95_btn: "PDF completo — 2 preguntas más",
    skip_to_lead: "Envíame lo que tengo ahora →",
    cta_h2: "Envíame el informe completo — gratis",
    cta_p: "Incluye todas las subvenciones de tu región. Sin tarjeta. Sin vendedores.",
    cta_wa_label: "WhatsApp", cta_wa_ph: "+34 ...",
    cta_em_label: "Email",    cta_em_ph: "tu@email.es",
    cta_send_wa: "Enviar a WhatsApp", cta_send_em: "Enviar a email", cta_or: "o",
    sent_ok: "Enviado — revisa tu bandeja/WhatsApp en 24h.",
    again: "Calcular de nuevo",
    upsell_h3: "¿Cuánto costaría una instalación de paneles solares para ti?",
    upsell_sub: "Recibe un presupuesto Dr. Sol sin compromiso.",
    upsell_btn: "Haz clic aquí →",
    err_q1: "Ajusta el importe a tu factura media",
    err_q2: "Introduce ciudad o código postal",
    err_pick: "Elige una opción",
    err_polygon: "Marca al menos 3 puntos en tu tejado",
    err_contact: "Introduce email o WhatsApp",
  },
  en: {
    nav_login: "Sign in",
    hero_pre: "DoctorSol Calculator",
    hero_h1_1: "Stop losing money,", hero_h1_2: "every single day.",
    hero_p: "The sun shines over Spain — yet you keep paying full price to the grid. Calculate in 60 seconds how much you're really losing.",
    hero_stat_label: "homeowners now know how much money they're losing", counter_live: "live",
    path_q: "Quick questions (60 sec)", path_bill: "Upload bill",
    upload_drop: "Drop your electricity bill here",
    upload_or: "or click to choose · PDF · JPG · PNG",
    q1_label: "What do you pay per month on average?", q1_suffix: "€/mo",
    q1_hint: "Almost all goes to the grid. Per year you lose",
    q2_label: "Where do you live?", q2_placeholder: "E.g. Madrid, Valencia, Marbella…",
    q3_label: "How many residents?",
    q4_label: "Home size?",
    q4_o1: "< 80 m²", q4_o2: "80-150 m²", q4_o3: "150-250 m²", q4_o4: "> 250 m²",
    q5_label: "Connection type (on your meter)",
    q5_o1_l: "Single-phase (mono)", q5_o1_s: "Most homes — up to ±9 kW",
    q5_o2_l: "Three-phase (trifásico)", q5_o2_s: "Often villas, ≥ 9.2 kW",
    q5_o3_l: "I don't know", q5_o3_s: "We'll use a conservative estimate",
    q6_label: "What kind of roof do you have?",
    q6_o1_l: "Flat roof",   q6_o1_s: "Panels on frame — ~92% yield",
    q6_o2_l: "Tilted roof", q6_o2_s: "Standard — 100% yield",
    q6_o3_l: "I don't know",q6_o3_s: "Conservative estimate",
    q7_label: "What will you still buy?",
    q7_hint: "What you already own is already in your bill — only tick future purchases.",
    q7_ev: "Electric car", q7_hp: "Heat pump", q7_pool: "Pool", q7_ac: "Air-con",
    q8_label: "Draw your roof on the map",
    q8_hint: "Search your address, zoom in to see your roof, and click the corners of the usable area.",
    q9_label: "Are you a tax resident in Spain?",
    q9_yes: "Yes, I live in Spain", q9_yes_s: "Eligible for IRPF deduction up to 60%",
    q9_no: "No, non-resident",     q9_no_s: "Still eligible for IBI bonus + EU funds",
    next: "Next", back: "Back",
    calc: "Calculate my savings", calc_busy: "Calculating…",
    refine: "Refine", refine_busy: "Refining…",
    premium: "Generate my PDF dossier", premium_busy: "Generating…",
    r1_kicker: "Your loss so far", r1_h1: "You're leaving this money on the table.",
    r1_p: "Estimated from your bill. Panels cover ~70% of consumption.",
    r2_kicker: "Refined calculation", r2_h1: "Your savings are bigger than we thought.",
    r2_p: "With your extra details we can refine — region, phase, future load.",
    r3_kicker: "Premium dossier · 95% accurate", r3_h1: "Your complete savings plan.",
    r3_p: "Includes subsidies (IRPF, IBI, Next Generation) and your actual roof area.",
    rev_month: "Per month", rev_year: "Per year", rev_10y: "Over 10 years",
    rev_subsidies: "Subsidies",
    rev_lost: "Money lost in past 12 months by not having panels:",
    delta_label: "Update",
    acc_label: "Accuracy",
    acc_60: "Rough estimate", acc_85: "Refined", acc_95: "Premium",
    upgrade_to_85_btn: "Refine to 85% — 3 more questions",
    upgrade_to_95_btn: "Full PDF — 2 more questions",
    skip_to_lead: "Send me what I have →",
    cta_h2: "Send me the full report — free",
    cta_p: "Includes every subsidy in your region. No credit card. No salespeople.",
    cta_wa_label: "WhatsApp", cta_wa_ph: "+34 ...",
    cta_em_label: "Email",    cta_em_ph: "you@email.es",
    cta_send_wa: "Send to WhatsApp", cta_send_em: "Send to email", cta_or: "or",
    sent_ok: "Sent — check your inbox/WhatsApp within 24h.",
    again: "Calculate again",
    upsell_h3: "What would a solar installation cost for you?",
    upsell_sub: "Get a free Dr. Sol quote — no obligation.",
    upsell_btn: "Click here →",
    err_q1: "Adjust the amount to your average bill",
    err_q2: "Enter your city or postcode",
    err_pick: "Pick an option",
    err_polygon: "Click at least 3 points on your roof",
    err_contact: "Enter email or WhatsApp",
  },
};
const t = (lang) => STR[(lang || "es").split("-")[0].toLowerCase()] || STR.es;

/* ───── ROI engine (front-end, transparent) ──────────────────────────────── */
function calcSavings({ monthlyEur, city, phase, roofType, futureApp, fiscalResident, polygon }) {
  const yearly = (Number(monthlyEur) || 0) * 12;
  const baseAnnual = yearly * 0.70;
  const c = (city || "").toLowerCase();
  const isAndalusia = /sevill|granad|malaga|cordoba|cadiz|huelva|jaen|almeri/.test(c);
  const isLevante   = /valencia|alican|murcia|cartagena|castell/.test(c);
  const isNorth     = /galici|asturi|cantab|coruna|bilbao|santand|navarr|euskadi|donost/.test(c);
  const regionMult  = isAndalusia ? 1.18 : isLevante ? 1.10 : isNorth ? 0.84 : 1.0;
  const phaseMult   = phase === "trifasico" ? 1.10 : 1.0;
  const roofMult    = roofType === "tilted" ? 1.05 : roofType === "flat" ? 0.92 : 1.0;
  const fa = futureApp || {};
  const extraKwh    = (fa.ev ? 2500 : 0) + (fa.heatpump ? 3500 : 0) + (fa.ac ? 1500 : 0) + (fa.pool ? 5000 : 0);
  const extraSave   = extraKwh * 0.18 * 0.70;
  const hasL2       = !!phase || !!roofType || Object.values(fa).some(Boolean);
  const annual      = hasL2 ? baseAnnual * regionMult * phaseMult * roofMult + extraSave : baseAnnual;
  let subsidies = 0;
  const hasPoly = polygon && polygon.length >= 3;
  if (hasPoly) {
    const systemCost = 6500;
    if (fiscalResident) subsidies += systemCost * 0.40;
    subsidies += 600;   // IBI 3 yrs
    subsidies += 1500;  // Next Gen avg
  }
  return {
    monthly_loss: Math.round(annual / 12),
    annual_save:  Math.round(annual),
    ten_year_save: Math.round(annual * 10 + subsidies),
    subsidies:    Math.round(subsidies),
  };
}

/* ───── Tiny animated € counter using framer-motion spring ───────────────── */
const AnimatedNum = ({ value, prefix = "€", suffix = "" }) => {
  const spring  = useSpring(0, { stiffness: 60, damping: 22, mass: 1 });
  const display = useTransform(spring, (v) => `${prefix}${Math.round(v).toLocaleString("es-ES")}${suffix}`);
  useEffect(() => { spring.set(value); }, [value, spring]);
  return <motion.span>{display}</motion.span>;
};

/* ───── Accuracy meter shown above reveals ───────────────────────────────── */
const AccuracyMeter = ({ pct, T }) => {
  const label = pct >= 95 ? T.acc_95 : pct >= 85 ? T.acc_85 : T.acc_60;
  const color = pct >= 95 ? "#16A34A" : pct >= 85 ? "#22C55E" : "#E78938";
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between text-xs uppercase tracking-wider font-bold mb-1.5" style={{ color }}>
        <span>{T.acc_label} · {label}</span>
        <span className="tabular-nums">{pct}%</span>
      </div>
      <div className="h-1.5 bg-[#0F172A]/8 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
};

export default function MockupHomeV4() {
  const { i18n } = useTranslation();
  const T = t(i18n.language);

  // View state machine
  const [view, setView] = useState("q1");
  const [submitting, setSubmitting] = useState(false);

  // Layer 1
  const [monthlyEur, setMonthlyEur] = useState(120);
  const [city, setCity]             = useState("");
  const [residents, setResidents]   = useState(3);
  const [homeSize, setHomeSize]     = useState("");
  // Layer 2
  const [phase, setPhase]           = useState("");
  const [roofType, setRoofType]     = useState("");
  const [futureApp, setFutureApp]   = useState({ ev: false, heatpump: false, pool: false, ac: false });
  // Layer 3
  const [polygon, setPolygon]       = useState([]);
  const [fiscalResident, setFiscalResident] = useState(null);
  // Contact (CTA)
  const [contactEmail, setContactEmail] = useState("");
  const [contactWa, setContactWa]       = useState("");
  const [sent, setSent]                 = useState(false);
  // For delta animation between r1 → r2 → r3
  const [prevTotal, setPrevTotal] = useState(0);

  // Counter: deterministic daily growth + live claim count.
  // Baseline 342 on 2026-02-17. Each day after baseline adds 20-50 (per-day
  // seeded so the value is stable across reloads on the same calendar day).
  const [counter, setCounter] = useState(() => {
    const baselineDate = new Date("2026-02-17T00:00:00Z");
    const today = new Date();
    const days = Math.max(0, Math.floor((today - baselineDate) / 86_400_000));
    let total = 342;
    for (let i = 0; i < days; i++) {
      const d = new Date(baselineDate.getTime() + i * 86_400_000).toISOString().slice(0, 10);
      let h = 0;
      for (const ch of d) h = (h * 31 + ch.charCodeAt(0)) | 0;
      total += 20 + (Math.abs(h) % 31); // 20-50
    }
    return total;
  });
  useEffect(() => {
    apiClient.get("/free-counter")
      .then((r) => setCounter((prev) => prev + Number(r?.data?.claimed || 0)))
      .catch(() => {});
  }, []);

  // Clarity session tagging — fires once at mount so the dashboard can filter
  // sessions by language / utm_source / device without needing per-event data.
  useEffect(() => {
    try {
      clarityTag("page", "home_v4");
      clarityTag("language", (i18n.language || "es").split("-")[0].toLowerCase());
      const qs = new URLSearchParams(window.location.search);
      const utmSource   = qs.get("utm_source");
      const utmMedium   = qs.get("utm_medium");
      const utmCampaign = qs.get("utm_campaign");
      const utmContent  = qs.get("utm_content");
      if (utmSource)   clarityTag("utm_source", utmSource);
      if (utmMedium)   clarityTag("utm_medium", utmMedium);
      if (utmCampaign) clarityTag("utm_campaign", utmCampaign);
      if (utmContent)  clarityTag("utm_content", utmContent);
    } catch { /* analytics never crashes UX */ }
  }, []);

  // Live savings
  const savings = useMemo(() => calcSavings({
    monthlyEur, city, phase, roofType, futureApp, fiscalResident, polygon,
  }), [monthlyEur, city, phase, roofType, futureApp, fiscalResident, polygon]);

  // Accuracy derived from data filled (not from current view), so leads from
  // Layer 3 are correctly tagged as `v4-95pct` even at the CTA submit screen.
  const accuracy = useMemo(() => {
    if (polygon && polygon.length >= 3) return 95;
    if (phase || roofType || Object.values(futureApp || {}).some(Boolean)) return 85;
    return 60;
  }, [polygon, phase, roofType, futureApp]);

  /* ───── Navigation handlers ─────────────────────────────────────────── */
  const advance = () => {
    if (view === "q1" && monthlyEur < 30)        { toast.error(T.err_q1); return; }
    if (view === "q2" && !city.trim())           { toast.error(T.err_q2); return; }
    if (view === "q4" && !homeSize)              { toast.error(T.err_pick); return; }
    if (view === "q5" && !phase)                 { toast.error(T.err_pick); return; }
    if (view === "q6" && !roofType)              { toast.error(T.err_pick); return; }
    if (view === "q8" && polygon.length < 3)     { toast.error(T.err_polygon); return; }
    if (view === "q9" && fiscalResident === null){ toast.error(T.err_pick); return; }

    const flow = {
      q1: "q2", q2: "q3", q3: "q4", q4: "do-r1",
      q5: "q6", q6: "q7", q7: "do-r2",
      q8: "q9", q9: "do-r3",
    };
    const next = flow[view];
    if (next === "do-r1") { doReveal("r1"); return; }
    if (next === "do-r2") { doReveal("r2"); return; }
    if (next === "do-r3") { doReveal("r3"); return; }
    setView(next);
  };

  const doReveal = (target) => {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setView(target);
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Tracking: layer-completion events
      const layer = target === "r1" ? 1 : target === "r2" ? 2 : 3;
      const event_name = `v4_layer_${layer}_complete`;
      const payload = {
        layer, monthly_eur: monthlyEur, city,
        annual_save: savings.annual_save, ten_year_save: savings.ten_year_save,
      };
      trackCustom(event_name, payload);
      gaTrackEvent(event_name, payload);
      clarityEvent(event_name);
      clarityTag("layer_reached", String(layer));
    }, 700);
  };

  const back = () => {
    const flow = { q2: "q1", q3: "q2", q4: "q3", q5: "r1", q6: "q5", q7: "q6", q8: "r2", q9: "q8" };
    setView(flow[view] || view);
  };

  /* ───── Contact submit (CTA) ────────────────────────────────────────── */
  const submitContact = async (channel) => {
    if (channel === "email" && !contactEmail.includes("@")) { toast.error(T.err_contact); return; }
    if (channel === "wa" && !contactWa.trim())              { toast.error(T.err_contact); return; }
    setSubmitting(true);
    try {
      const lang = (i18n.language || "es").split("-")[0].toLowerCase();
      await apiClient.post("/leads", {
        name: contactEmail.split("@")[0] || "v4-lead",
        email: contactEmail || `${contactWa.replace(/\D/g, "")}@whatsapp.placeholder`,
        whatsapp: contactWa || "—",
        region: city || "madrid",
        language: lang,
        funnel: `v4-${accuracy}pct`,
        intake_preview: {
          layer: accuracy === 95 ? 3 : accuracy === 85 ? 2 : 1,
          monthly_eur: monthlyEur, city, residents, home_size: homeSize,
          phase, roof_type: roofType, future_app: futureApp,
          fiscal_resident: fiscalResident,
          annual_save: savings.annual_save,
          ten_year_save: savings.ten_year_save,
          subsidies: savings.subsidies,
        },
      });
      setSent(true);
      // Tracking: lead converted via calculator CTA
      const leadPayload = {
        content_name: "DoctorSol V4 Calculator", channel,
        layer: accuracy === 95 ? 3 : accuracy === 85 ? 2 : 1,
        value: savings.ten_year_save, currency: "EUR",
      };
      fbTrackLead(leadPayload);
      gaTrackLead({ ...leadPayload, value: savings.ten_year_save });
      // Clarity: tag this session as a lead, upgrade priority, mark event
      clarityTag("funnel", `v4-${accuracy}pct`);
      clarityTag("lead_channel", channel);
      clarityTag("lead_status", "converted");
      clarityUpgrade("lead_converted");
      clarityEvent("v4_lead_submitted");
      toast.success(T.sent_ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  /* ───── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", WebkitFontSmoothing: "antialiased", letterSpacing: "-0.01em" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');
        .h-display { font-family: 'Outfit', sans-serif; font-weight: 800; letter-spacing: -0.04em; line-height: 0.95; }
        .h-display-thin { font-family: 'Outfit', sans-serif; font-weight: 600; letter-spacing: -0.035em; }
        @keyframes pulse-red { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:.55; transform: scale(1.4);} }
        .pulse-red { animation: pulse-red 1.4s ease-in-out infinite; }
        @keyframes drift { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .drift { animation: drift 7s ease-in-out infinite; }
      `}</style>

      <header className="px-5 md:px-12 lg:px-20 py-5 flex items-center justify-between border-b border-[#0F172A]/5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#E78938] flex items-center justify-center"><Sun className="w-5 h-5 text-white" /></div>
          <span className="h-display-thin text-xl">DoctorSol</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="/over-ons" data-testid="nav-about"
            className="hidden sm:inline text-sm text-[#475569] hover:text-[#0F172A]">
            {i18n.language?.startsWith("es") ? "Sobre nosotros" : i18n.language?.startsWith("en") ? "About" : "Over ons"}
          </a>
          <LanguageSwitcher variant="minimal" />
          <a href="/portal" className="text-sm text-[#475569] hover:text-[#0F172A]" data-testid="nav-login">{T.nav_login}</a>
        </div>
      </header>

      {/* HERO + embedded card */}
      <section className="px-5 md:px-12 lg:px-20 pt-10 md:pt-16 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left copy */}
          <div className="lg:col-span-6 lg:pr-4">
            <div className="inline-flex items-center gap-2 bg-[#DC2626]/10 text-[#DC2626] px-3.5 py-1.5 rounded-full text-sm font-semibold border border-[#DC2626]/20 mb-7">
              <span className="w-2 h-2 bg-[#DC2626] rounded-full pulse-red" />
              {T.hero_pre}
            </div>
            <h1 className="h-display text-[44px] sm:text-[56px] lg:text-[72px] mb-6">
              <span className="text-[#0F172A]">{T.hero_h1_1}</span><br/>
              <span className="text-[#DC2626] inline-flex items-center gap-3">
                {T.hero_h1_2}
                <TrendingDown className="w-10 h-10 lg:w-14 lg:h-14 text-[#DC2626] -mb-2" strokeWidth={2.6} />
              </span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed max-w-xl mb-8">{T.hero_p}</p>
            {!sent ? (
              <div className="inline-flex items-center gap-3 bg-white border border-[#0F172A]/10 rounded-2xl px-5 py-4 shadow-sm">
                <div className="w-10 h-10 rounded-full bg-[#16A34A]/10 flex items-center justify-center"><Check className="w-5 h-5 text-[#16A34A]" /></div>
                <div>
                  <div className="h-display text-2xl text-[#0F172A] tabular-nums" data-testid="trust-counter">{counter.toLocaleString("es-ES")}</div>
                  <div className="text-sm text-[#475569]">
                    {T.hero_stat_label} <span className="text-[#16A34A] font-semibold uppercase ml-1">· {T.counter_live}</span>
                  </div>
                </div>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="bg-white border-2 border-[#E78938] rounded-2xl px-5 py-4 shadow-md max-w-md">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#E78938]/15 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-[#E78938]" />
                  </div>
                  <div className="flex-1">
                    <div className="h-display-thin text-base text-[#0F172A] mb-0.5 leading-snug">{T.upsell_h3}</div>
                    <div className="text-sm text-[#475569]">{T.upsell_sub}</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    try {
                      localStorage.setItem("doctorsol_quote_request", JSON.stringify({
                        monthly_eur: monthlyEur, city, residents, home_size: homeSize,
                        phase, roof_type: roofType, future_app: futureApp,
                        polygon, fiscal_resident: fiscalResident,
                        savings, language: i18n.language, ts: Date.now(),
                      }));
                    } catch (e) { /* localStorage unavailable */ }
                    trackInitiateCheckout({ content_name: "DoctorSol Quote Request (post-lead)", value: savings.ten_year_save, currency: "EUR" });
                    gaTrackEvent("v4_upsell_click_post_lead", { value: savings.ten_year_save });
                    window.location.href = "/offerte";
                  }}
                  data-testid="hero-upsell-after-sent"
                  className="w-full bg-[#E78938] hover:bg-[#D17627] text-white rounded-full px-5 py-3 font-semibold text-base transition-colors flex items-center justify-center gap-2">
                  {T.upsell_btn}
                </button>
              </motion.div>
            )}
          </div>

          {/* Right card */}
          <div className="lg:col-span-6">
            <div className="bg-white rounded-3xl shadow-[0_24px_64px_-12px_rgba(15,23,42,0.18)] border border-[#0F172A]/5 overflow-hidden">
              <div className="p-7 md:p-9">
                <AnimatePresence mode="wait" initial={false}>
                  {/* ────── LAYER 1 ────── */}
                  {["q1","q2","q3","q4"].includes(view) && (
                    <motion.div key={view}
                      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.3 }} className="space-y-6">
                      {view === "q1" && <Q1 T={T} value={monthlyEur} onChange={setMonthlyEur} />}
                      {view === "q2" && <Q2 T={T} value={city} onChange={setCity} />}
                      {view === "q3" && <Q3 T={T} value={residents} onChange={setResidents} />}
                      {view === "q4" && <Q4 T={T} value={homeSize} onChange={setHomeSize} />}
                      <NavRow T={T} step={["q1","q2","q3","q4"].indexOf(view) + 1} total={4} onNext={advance} onBack={back} canBack={view !== "q1"} submitting={submitting} lastLabel={T.calc} lastLabelBusy={T.calc_busy} />
                    </motion.div>
                  )}

                  {/* ────── REVEAL 1 ────── */}
                  {view === "r1" && (
                    <RevealCard T={T} kicker={T.r1_kicker} h1={T.r1_h1} p={T.r1_p} savings={savings} accuracy={60}
                      prevTotal={0}
                      primaryLabel={T.upgrade_to_85_btn}
                      onPrimary={() => { setPrevTotal(savings.ten_year_save); setView("q5"); }}
                      primaryTestid="upgrade-l2"
                      secondaryLabel={T.skip_to_lead} onSecondary={() => setView("cta")} secondaryTestid="skip-to-cta-l1" />
                  )}

                  {/* ────── LAYER 2 ────── */}
                  {["q5","q6","q7"].includes(view) && (
                    <motion.div key={view}
                      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.3 }} className="space-y-6">
                      <AccuracyMeter pct={60 + (["q5","q6","q7"].indexOf(view) + 1) * 8} T={T} />
                      {view === "q5" && <PickList T={T} label={T.q5_label} options={[
                        { v: "monofasico", l: T.q5_o1_l, s: T.q5_o1_s },
                        { v: "trifasico",  l: T.q5_o2_l, s: T.q5_o2_s },
                        { v: "unknown",    l: T.q5_o3_l, s: T.q5_o3_s },
                      ]} value={phase} onChange={setPhase} testid="q5" />}
                      {view === "q6" && <PickList T={T} label={T.q6_label} options={[
                        { v: "flat",    l: T.q6_o1_l, s: T.q6_o1_s, icon: Sun },
                        { v: "tilted",  l: T.q6_o2_l, s: T.q6_o2_s, icon: Home },
                        { v: "unknown", l: T.q6_o3_l, s: T.q6_o3_s },
                      ]} value={roofType} onChange={setRoofType} testid="q6" />}
                      {view === "q7" && <Q7 T={T} value={futureApp} onChange={setFutureApp} />}
                      <NavRow T={T} step={["q5","q6","q7"].indexOf(view) + 1} total={3} onNext={advance} onBack={back} canBack submitting={submitting} lastLabel={T.refine} lastLabelBusy={T.refine_busy} />
                    </motion.div>
                  )}

                  {/* ────── REVEAL 2 ────── */}
                  {view === "r2" && (
                    <RevealCard T={T} kicker={T.r2_kicker} h1={T.r2_h1} p={T.r2_p} savings={savings} accuracy={85}
                      prevTotal={prevTotal}
                      primaryLabel={T.upgrade_to_95_btn}
                      onPrimary={() => { setPrevTotal(savings.ten_year_save); setView("q8"); }}
                      primaryTestid="upgrade-l3"
                      secondaryLabel={T.skip_to_lead} onSecondary={() => setView("cta")} secondaryTestid="skip-to-cta-l2" />
                  )}

                  {/* ────── LAYER 3 ────── */}
                  {view === "q8" && (
                    <motion.div key="q8"
                      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.3 }} className="space-y-5">
                      <AccuracyMeter pct={88} T={T} />
                      <div>
                        <label className="text-base font-semibold text-[#0F172A] block mb-2">{T.q8_label}</label>
                        <p className="text-sm text-[#94A3B8] mb-3">{T.q8_hint}</p>
                        <RoofMap initial={polygon} onChange={setPolygon} initialAddress={city} />
                      </div>
                      <NavRow T={T} step={1} total={2} onNext={advance} onBack={back} canBack submitting={submitting} lastLabel={T.next} lastLabelBusy={T.calc_busy} />
                    </motion.div>
                  )}
                  {view === "q9" && (
                    <motion.div key="q9"
                      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                      transition={{ duration: 0.3 }} className="space-y-6">
                      <AccuracyMeter pct={92} T={T} />
                      <PickList T={T} label={T.q9_label} options={[
                        { v: "yes", l: T.q9_yes, s: T.q9_yes_s },
                        { v: "no",  l: T.q9_no,  s: T.q9_no_s  },
                      ]} value={fiscalResident === true ? "yes" : fiscalResident === false ? "no" : ""}
                         onChange={(v) => setFiscalResident(v === "yes")} testid="q9" />
                      <NavRow T={T} step={2} total={2} onNext={advance} onBack={back} canBack submitting={submitting} lastLabel={T.premium} lastLabelBusy={T.premium_busy} />
                    </motion.div>
                  )}

                  {/* ────── REVEAL 3 ────── */}
                  {view === "r3" && (
                    <RevealCard T={T} kicker={T.r3_kicker} h1={T.r3_h1} p={T.r3_p} savings={savings} accuracy={95}
                      prevTotal={prevTotal} showSubsidies
                      primaryLabel={T.cta_send_em} onPrimary={() => setView("cta")} primaryTestid="reveal3-to-cta"
                      secondaryLabel={null} onSecondary={null} />
                  )}

                  {/* ────── CTA (lead capture) ────── */}
                  {view === "cta" && !sent && (
                    <motion.div key="cta"
                      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }} className="space-y-5">
                      <AccuracyMeter pct={accuracy} T={T} />
                      <div>
                        <h2 className="h-display text-2xl md:text-3xl mb-2">{T.cta_h2}</h2>
                        <p className="text-sm text-[#475569] leading-relaxed">{T.cta_p}</p>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div className="bg-[#F8F6F0] border border-[#0F172A]/5 rounded-2xl p-4">
                          <label className="text-base font-semibold text-[#0F172A] block mb-2">{T.cta_wa_label}</label>
                          <input type="tel" value={contactWa} onChange={(e) => setContactWa(e.target.value)}
                            placeholder={T.cta_wa_ph} data-testid="cta-wa-input"
                            className="w-full bg-white border border-[#0F172A]/10 rounded-lg px-3.5 py-3 text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20 mb-3" />
                          <button onClick={() => submitContact("wa")} disabled={submitting} data-testid="cta-wa-btn"
                            className="w-full bg-[#25D366] hover:bg-[#1ebd5b] text-white rounded-lg py-3 font-semibold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                            <MessageCircle className="w-4 h-4" /> {T.cta_send_wa}
                          </button>
                        </div>
                        <div className="text-xs uppercase tracking-wider text-[#94A3B8] text-center">{T.cta_or}</div>
                        <div className="bg-[#F8F6F0] border border-[#0F172A]/5 rounded-2xl p-4">
                          <label className="text-base font-semibold text-[#0F172A] block mb-2">{T.cta_em_label}</label>
                          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                            placeholder={T.cta_em_ph} data-testid="cta-em-input"
                            className="w-full bg-white border border-[#0F172A]/10 rounded-lg px-3.5 py-3 text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#E78938] focus:ring-2 focus:ring-[#E78938]/20 mb-3" />
                          <button onClick={() => submitContact("email")} disabled={submitting} data-testid="cta-em-btn"
                            className="w-full bg-[#E78938] hover:bg-[#D17627] text-white rounded-lg py-3 font-semibold text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
                            <Mail className="w-4 h-4" /> {T.cta_send_em}
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* ────── SENT ────── */}
                  {sent && (
                    <motion.div key="sent" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }} className="text-center py-8">
                      <div className="w-16 h-16 bg-[#16A34A] rounded-full flex items-center justify-center mx-auto mb-5"><Check className="w-8 h-8 text-white" /></div>
                      <h2 className="h-display text-2xl md:text-3xl mb-2">{T.sent_ok}</h2>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom upsell banner — appears AFTER the user submitted email/WhatsApp.
          Reinforces the Dr. Sol quote CTA alongside the hero swap. */}
      {sent && (
        <motion.section
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          className="px-5 md:px-12 lg:px-20 py-16 md:py-20">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#E78938] to-[#D17627] text-white rounded-3xl p-8 md:p-12 flex flex-col md:flex-row gap-6 md:items-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center shrink-0 drift"><Zap className="w-8 h-8" /></div>
            <div className="flex-1">
              <h3 className="h-display text-2xl md:text-3xl mb-2">{T.upsell_h3}</h3>
              <p className="text-base text-white/85">{T.upsell_sub}</p>
            </div>
            <button onClick={() => {
                try {
                  localStorage.setItem("doctorsol_quote_request", JSON.stringify({
                    monthly_eur: monthlyEur, city, residents, home_size: homeSize,
                    phase, roof_type: roofType, future_app: futureApp,
                    polygon, fiscal_resident: fiscalResident,
                    savings, language: i18n.language, ts: Date.now(),
                  }));
                } catch (e) { /* localStorage unavailable */ }
                // Tracking: user moved to quote-request funnel
                const payload = { content_name: "DoctorSol Quote Request", value: savings.ten_year_save, currency: "EUR" };
                trackInitiateCheckout(payload);
                gaTrackEvent("v4_upsell_click", payload);
                window.location.href = "/offerte";
              }}
              data-testid="upsell-link"
              className="shrink-0 bg-white text-[#0F172A] rounded-full px-7 py-3.5 font-semibold text-base hover:bg-[#F8F6F0] transition-colors flex items-center gap-2">
              {T.upsell_btn}
            </button>
          </div>
        </motion.section>
      )}

      <footer className="border-t border-[#0F172A]/10 px-5 md:px-12 lg:px-20 py-8 text-sm text-[#94A3B8] flex flex-wrap justify-between gap-3">
        <div>© 2026 Recywatt Iberica S.L. · DoctorSol</div>
        <div className="flex gap-5">
          <a href="/over-ons">{i18n.language?.startsWith("es") ? "Sobre nosotros" : i18n.language?.startsWith("en") ? "About" : "Over ons"}</a>
          <a href="/privacy">Privacy</a><a href="/voorwaarden">Voorwaarden</a><a href="mailto:info@doctorsol.es">Contact</a>
        </div>
      </footer>
    </div>
  );
}

/* ───── Reusable bits ───────────────────────────────────────────────────── */
const NavRow = ({ T, step, total, onNext, onBack, canBack, submitting, lastLabel, lastLabelBusy }) => (
  <div className="flex items-center justify-between pt-2">
    <div className="flex items-center gap-3">
      {canBack && (
        <button onClick={onBack} data-testid="nav-back"
          className="text-sm text-[#475569] hover:text-[#0F172A] flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> {T.back}
        </button>
      )}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`w-7 h-1.5 rounded-full transition-all ${i < step ? "bg-[#E78938]" : "bg-[#0F172A]/10"}`} />
        ))}
      </div>
    </div>
    <button onClick={onNext} disabled={submitting} data-testid="q-next"
      className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 py-3 font-semibold text-base transition-all disabled:opacity-50">
      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
      {step === total ? (submitting ? lastLabelBusy : lastLabel) : T.next}
      {!submitting && <ChevronRight className="w-4 h-4" />}
    </button>
  </div>
);

/* ───── Reveal card with optional delta-pop ─────────────────────────────── */
const RevealCard = ({ T, kicker, h1, p, savings, accuracy, prevTotal, showSubsidies,
                     primaryLabel, onPrimary, primaryTestid,
                     secondaryLabel, onSecondary, secondaryTestid }) => {
  const delta = prevTotal > 0 ? savings.ten_year_save - prevTotal : 0;
  return (
    <motion.div key={`rev-${accuracy}`}
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}>
      <AccuracyMeter pct={accuracy} T={T} />
      <div className="text-xs uppercase tracking-wider font-bold flex items-center gap-2 mb-3"
           style={{ color: accuracy >= 95 ? "#16A34A" : accuracy >= 85 ? "#22C55E" : "#DC2626" }}>
        {accuracy >= 95 ? <Award className="w-3.5 h-3.5" /> :
         accuracy >= 85 ? <TrendingUp className="w-3.5 h-3.5" /> :
         <AlertTriangle className="w-3.5 h-3.5" />}
        {kicker}
      </div>
      <h2 className="h-display text-2xl md:text-3xl mb-3">{h1}</h2>
      <p className="text-sm text-[#475569] mb-5">{p}</p>

      {delta > 0 && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="inline-flex items-center gap-2 bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 rounded-full px-3.5 py-1.5 text-xs font-bold mb-5">
          <ArrowUp className="w-3.5 h-3.5" /> {T.delta_label}: +€{delta.toLocaleString("es-ES")}
        </motion.div>
      )}

      <div className="space-y-3 mb-6">
        <RevealRow label={T.rev_month} value={savings.monthly_loss} />
        <RevealRow label={T.rev_year}  value={savings.annual_save} />
        {showSubsidies && savings.subsidies > 0 && (
          <RevealRow label={T.rev_subsidies} value={savings.subsidies} extra />
        )}
        <RevealRow label={T.rev_10y}   value={savings.ten_year_save} hero />
      </div>

      <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-4 text-base text-[#7F1D1D] mb-7 flex items-start gap-3">
        <TrendingDown className="w-5 h-5 shrink-0 mt-0.5 text-[#DC2626]" />
        <div>
          <strong className="block text-[#DC2626]">{T.rev_lost}</strong>
          <span className="h-display text-2xl text-[#DC2626] block mt-1 tabular-nums">
            <AnimatedNum value={savings.annual_save} />
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <button onClick={onPrimary} data-testid={primaryTestid}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full px-6 py-3.5 font-semibold text-sm transition-all">
          {accuracy >= 95 ? <FileText className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
          {primaryLabel} <ChevronRight className="w-4 h-4" />
        </button>
        {secondaryLabel && (
          <button onClick={onSecondary} data-testid={secondaryTestid}
            className="text-sm text-[#475569] hover:text-[#0F172A] py-2 transition-colors">
            {secondaryLabel}
          </button>
        )}
      </div>
    </motion.div>
  );
};

const RevealRow = ({ label, value, hero, extra }) => (
  <div className={`flex items-baseline justify-between border-b border-[#0F172A]/5 pb-3 ${hero ? "pt-3" : ""}`}>
    <span className={`text-base text-[#475569] ${hero ? "uppercase tracking-wider font-semibold text-xs" : ""}`}>{label}</span>
    <span className={`tabular-nums ${hero ? "h-display text-4xl md:text-5xl text-[#16A34A]" : extra ? "h-display-thin text-xl text-[#22C55E]" : "h-display-thin text-2xl text-[#0F172A]"}`}>
      <AnimatedNum value={value} />
    </span>
  </div>
);

/* ───── Questions ───────────────────────────────────────────────────────── */
const Q1 = ({ T, value, onChange }) => {
  const yearly = Math.round((Number(value) || 0) * 12);
  return (
    <div>
      <label className="text-base font-semibold text-[#0F172A] block mb-4">{T.q1_label}</label>
      <div className="text-center mb-5">
        <span className="h-display text-6xl text-[#0F172A] tabular-nums">€{value}</span>
        <span className="text-[#475569] text-lg ml-1">{T.q1_suffix}</span>
      </div>
      <input type="range" min={30} max={500} step={5} value={value}
        onChange={(e) => onChange(Number(e.target.value))} data-testid="q1-slider"
        className="w-full accent-[#E78938] h-2" />
      <div className="flex justify-between text-sm text-[#94A3B8] mt-2"><span>€30</span><span>€250</span><span>€500</span></div>
      <div className="mt-5 bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-4 text-base text-[#7F1D1D]">
        {T.q1_hint} <strong className="ml-1 tabular-nums">€{yearly.toLocaleString("es-ES")}</strong>
      </div>
    </div>
  );
};

const Q2 = ({ T, value, onChange }) => (
  <div>
    <label className="text-base font-semibold text-[#0F172A] block mb-4">{T.q2_label}</label>
    <div className="relative">
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={T.q2_placeholder} data-testid="q2-input" autoFocus
        className="w-full bg-[#F8F6F0] border-2 border-transparent rounded-2xl pl-12 pr-4 py-4 text-lg focus:outline-none focus:border-[#E78938]" />
    </div>
  </div>
);

const Q3 = ({ T, value, onChange }) => (
  <div>
    <label className="text-base font-semibold text-[#0F172A] block mb-4">{T.q3_label}</label>
    <div className="flex items-center justify-center gap-3">
      {[1,2,3,4,5,6].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`q3-${n}`}
          className={`w-14 h-14 rounded-2xl border-2 text-xl font-bold transition-all ${value === n ? "border-[#E78938] bg-[#E78938] text-white scale-105" : "border-[#0F172A]/10 hover:border-[#E78938]/50 text-[#0F172A]"}`}>
          {n === 6 ? "6+" : n}
        </button>
      ))}
    </div>
    <div className="flex items-center justify-center gap-2 mt-4 text-[#475569] text-sm">
      <Users className="w-4 h-4" /> {value === 6 ? "6+" : value}
    </div>
  </div>
);

const Q4 = ({ T, value, onChange }) => {
  const opts = [{k:"o1",l:T.q4_o1},{k:"o2",l:T.q4_o2},{k:"o3",l:T.q4_o3},{k:"o4",l:T.q4_o4}];
  return (
    <div>
      <label className="text-base font-semibold text-[#0F172A] block mb-4">{T.q4_label}</label>
      <div className="grid grid-cols-2 gap-3">
        {opts.map((o) => (
          <button key={o.k} type="button" onClick={() => onChange(o.k)} data-testid={`q4-${o.k}`}
            className={`p-5 rounded-2xl border-2 text-base font-semibold flex items-center justify-center gap-2 ${value === o.k ? "border-[#E78938] bg-[#E78938]/5 text-[#0F172A]" : "border-[#0F172A]/10 hover:border-[#E78938]/40 bg-white text-[#475569]"}`}>
            <Home className="w-4 h-4 shrink-0" /> {o.l}
          </button>
        ))}
      </div>
    </div>
  );
};

/* Generic 1-of-N pick list for Q5/Q6/Q9 */
const PickList = ({ T, label, options, value, onChange, testid }) => (
  <div>
    <label className="text-base font-semibold text-[#0F172A] block mb-4">{label}</label>
    <div className="space-y-2.5">
      {options.map((o) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} data-testid={`${testid}-${o.v}`}
          className={`w-full flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all ${value === o.v ? "border-[#E78938] bg-[#E78938]/5" : "border-[#0F172A]/10 hover:border-[#E78938]/40 bg-white"}`}>
          <div className={`w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 ${value === o.v ? "border-[#E78938] bg-[#E78938]" : "border-[#0F172A]/30"}`}>
            {value === o.v && <div className="w-2 h-2 bg-white rounded-full" />}
          </div>
          <div>
            <div className="h-display-thin text-base font-semibold">{o.l}</div>
            <div className="text-sm text-[#475569] mt-0.5">{o.s}</div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const Q7 = ({ T, value, onChange }) => {
  const chips = [
    { k: "ev",       label: T.q7_ev,   icon: Car },
    { k: "heatpump", label: T.q7_hp,   icon: Flame },
    { k: "pool",     label: T.q7_pool, icon: Waves },
    { k: "ac",       label: T.q7_ac,   icon: Wind },
  ];
  return (
    <div>
      <label className="text-base font-semibold text-[#0F172A] block mb-3">{T.q7_label}</label>
      <p className="text-sm text-[#94A3B8] mb-4 italic">↳ {T.q7_hint}</p>
      <div className="flex flex-wrap gap-2.5">
        {chips.map((c) => {
          const on = !!value[c.k];
          return (
            <button key={c.k} type="button" onClick={() => onChange({ ...value, [c.k]: !on })} data-testid={`q7-${c.k}`}
              className={`flex items-center gap-2 px-5 py-3 rounded-full border-2 text-base font-medium transition-all ${on ? "border-[#E78938] bg-[#E78938] text-white shadow-md" : "border-[#0F172A]/10 hover:border-[#E78938]/40 bg-white"}`}>
              <c.icon className="w-4 h-4" /> {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
